import {
  Add as AddIcon,
  ArrowBack,
  Download as DownloadIcon,
  Edit as EditIcon,
  QrCode as QrCodeIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Asset, ServiceEvent } from '../types';

const statusColors: Record<string, 'success' | 'warning' | 'error'> = {
  active: 'success',
  maintenance: 'warning',
  retired: 'error',
};

const eventTypeLabels: Record<string, string> = {
  service: 'Service',
  repair: 'Repair',
  inspection: 'Inspection',
  manual_update: 'Manual Update',
  install: 'Installation',
  status_change: 'Status Change',
};

export function AssetDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [history, setHistory] = useState<ServiceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eventForm, setEventForm] = useState({
    event_type: 'service' as const,
    description: '',
  });
  const [editForm, setEditForm] = useState({
    serial_number: '',
    type: '',
    status: 'active' as 'active' | 'maintenance' | 'retired',
  });

  async function fetchAssetDetails() {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from('assets')
        .select(
          `
          id, customer_id, tag_id, serial_number, type, status, created_at, updated_at,
          customer:customers(id, name, email, phone, address)
        `
        )
        .eq('id', id)
        .single();

      if (error) throw error;
      setAsset(data as Asset);
    } catch (error) {
      console.error('Error fetching asset:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchHistory() {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from('asset_histories')
        .select('id, asset_id, event_type, description, performed_by, event_at, metadata')
        .eq('asset_id', id)
        .order('event_at', { ascending: false });

      if (error) throw error;
      setHistory((data as ServiceEvent[]) || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }

  useEffect(() => {
    if (id) {
      fetchAssetDetails();
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAddEvent = async () => {
    if (!id) return;
    try {
      const { error } = await supabase.from('asset_histories').insert({
        asset_id: id,
        event_type: eventForm.event_type,
        description: eventForm.description || null,
        event_at: new Date().toISOString(),
      });

      if (error) throw error;

      setEventDialogOpen(false);
      setEventForm({ event_type: 'service', description: '' });
      fetchHistory();
    } catch (error) {
      console.error('Error adding event:', error);
    }
  };

  const handleViewQR = async () => {
    if (!asset) return;
    setQrDialogOpen(true);
    setQrLoading(true);

    try {
      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
      const qrContent = `${appUrl}/asset/${asset.tag_id}`;

      const dataUrl = await QRCode.toDataURL(qrContent, {
        width: 400,
        margin: 2,
        errorCorrectionLevel: 'M',
      });

      setQrDataUrl(dataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setQrLoading(false);
    }
  };

  const handleDownloadQR = () => {
    if (!qrDataUrl || !asset) return;

    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `qr-${asset.tag_id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenEdit = () => {
    if (!asset) return;
    setEditForm({
      serial_number: asset.serial_number || '',
      type: asset.type || '',
      status: asset.status,
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('assets')
        .update({
          serial_number: editForm.serial_number || null,
          type: editForm.type || null,
          status: editForm.status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      // Log the edit event
      await supabase.from('asset_histories').insert({
        asset_id: id,
        event_type: 'manual_update',
        description: `Asset updated: status=${editForm.status}, type=${editForm.type || 'none'}, serial=${editForm.serial_number || 'none'}`,
        event_at: new Date().toISOString(),
      });

      setEditDialogOpen(false);
      fetchAssetDetails();
      fetchHistory();
    } catch (error) {
      console.error('Error updating asset:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" height={200} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!asset) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Asset not found
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/assets')}>
          Back to Assets
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <IconButton onClick={() => navigate('/assets')} sx={{ mr: { xs: 0, sm: 2 } }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
          <Typography variant="h4" component="h1" fontWeight={600} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            {asset.serial_number || asset.tag_id}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {asset.type || 'No type specified'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Button 
            variant="outlined" 
            startIcon={<QrCodeIcon sx={{ display: { xs: 'none', sm: 'inline' } }} />} 
            onClick={handleViewQR}
            size="small"
            sx={{ flex: { xs: 1, sm: 'initial' } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>View QR</Box>
            <QrCodeIcon sx={{ display: { xs: 'inline', sm: 'none' } }} />
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<EditIcon sx={{ display: { xs: 'none', sm: 'inline' } }} />} 
            onClick={handleOpenEdit}
            size="small"
            sx={{ flex: { xs: 1, sm: 'initial' } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Edit</Box>
            <EditIcon sx={{ display: { xs: 'inline', sm: 'none' } }} />
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Asset Details
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      label={asset.status}
                      size="small"
                      color={statusColors[asset.status] || 'default'}
                    />
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Tag ID
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {asset.tag_id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Serial Number
                  </Typography>
                  <Typography variant="body2">{asset.serial_number || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="body2">{asset.type || '-'}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body2">{asset.customer?.name || '-'}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {new Date(asset.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Manuals
              </Typography>
              <Button variant="outlined" startIcon={<UploadIcon />} fullWidth>
                Upload Manual
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Service History</Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setEventDialogOpen(true)}
                >
                  Add Event
                </Button>
              </Box>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: { xs: 'auto', sm: 400 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No service history recorded
                        </TableCell>
                      </TableRow>
                    ) : (
                      history.map(event => (
                        <TableRow key={event.id}>
                          <TableCell sx={{ minWidth: 80 }}>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {new Date(event.event_at).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 100 }}>
                            <Chip
                              label={eventTypeLabels[event.event_type] || event.event_type}
                              size="small"
                              variant="outlined"
                            />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                display: { xs: 'block', sm: 'none' }, 
                                mt: 0.5,
                                fontSize: '0.75rem',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                              }}
                            >
                              {event.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Typography 
                              variant="body2"
                              sx={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                              }}
                            >
                              {event.description || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={eventDialogOpen}
        onClose={() => setEventDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Service Event</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={eventForm.event_type}
                label="Event Type"
                onChange={e =>
                  setEventForm({
                    ...eventForm,
                    event_type: e.target.value as typeof eventForm.event_type,
                  })
                }
              >
                <MenuItem value="service">Service</MenuItem>
                <MenuItem value="repair">Repair</MenuItem>
                <MenuItem value="inspection">Inspection</MenuItem>
                <MenuItem value="install">Installation</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Description"
              value={eventForm.description}
              onChange={e => setEventForm({ ...eventForm, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddEvent}>
            Add Event
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Asset QR Code</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {qrLoading ? (
              <CircularProgress />
            ) : qrDataUrl ? (
              <>
                <img src={qrDataUrl} alt="QR Code" style={{ maxWidth: '100%', height: 'auto' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Scan this code to view asset information
                </Typography>
                <Typography variant="caption" fontFamily="monospace" display="block" sx={{ mt: 1 }}>
                  {asset?.tag_id}
                </Typography>
              </>
            ) : (
              <Typography color="error">Failed to generate QR code</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadQR}
            disabled={!qrDataUrl}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Asset Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Asset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Serial Number"
              value={editForm.serial_number}
              onChange={e => setEditForm({ ...editForm, serial_number: e.target.value })}
              fullWidth
            />
            <TextField
              label="Type"
              value={editForm.type}
              onChange={e => setEditForm({ ...editForm, type: e.target.value })}
              fullWidth
              placeholder="e.g., Fire Extinguisher, HVAC Unit"
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status}
                label="Status"
                onChange={e =>
                  setEditForm({
                    ...editForm,
                    status: e.target.value as typeof editForm.status,
                  })
                }
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="retired">Retired</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
